/**
 * What may leave the browser inside an error report.
 *
 * This is the client's copy of server/api-util/sentryScrub.js, which explains
 * the rules at length. It is a copy because the client bundle cannot import
 * from server/; sentryScrub.parity.test.js fails if the key and header lists
 * drift apart, so the two cannot quietly disagree about what is sensitive.
 *
 * The one difference is free-text scrubbing. The server reuses piiScrub; here a
 * short list of patterns covers what shows up in a browser — emails, phone
 * numbers, Canadian postal codes (the postal search puts them in URLs), bearer
 * tokens and JWTs.
 *
 * Fail closed: `scrubEventOrDrop` returns null if scrubbing throws, so a strange
 * event is dropped rather than sent unscrubbed.
 */

export const REDACTED = '[Filtered]';

export const SENSITIVE_KEY = /pass(?:word|wd|code|phrase)?|secret|token|authori[sz]ation|cookie|api[-_]?key|licen[cs]e|ssn|\bsin\b|passport|document|birth|\bdob\b|card|cvv|cvc|iban|account[-_]?number|session|credential|signature|private/i;

export const SENSITIVE_HEADERS = [
  'authorization',
  'proxy-authorization',
  'cookie',
  'set-cookie',
  'x-admin-secret',
  'x-admin-actor',
  'x-api-key',
  'x-csrf-token',
  'x-forwarded-for',
  'x-real-ip',
];

const MAX_DEPTH = 8;

const TEXT_PATTERNS = [
  { label: 'jwt', pattern: /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g },
  { label: 'token', pattern: /\bBearer\s+[A-Za-z0-9._~+/=-]{8,}/gi },
  { label: 'email', pattern: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g },
  {
    label: 'phone',
    pattern: /(?:\+?1[\s.-]?)?(?:\(\d{3}\)|\b\d{3})[\s.-]?\d{3}[\s.-]?\d{4}\b/g,
  },
  { label: 'postal-code', pattern: /\b[A-Za-z]\d[A-Za-z][\s-]?\d[A-Za-z]\d\b/g },
];

export const scrubText = input => {
  if (typeof input !== 'string' || !input) return input;
  return TEXT_PATTERNS.reduce(
    (text, { label, pattern }) => text.replace(pattern, `[redacted-${label}]`),
    input
  );
};

const redact = (value, depth = 0, seen = new WeakSet()) => {
  if (typeof value === 'string') return scrubText(value);
  if (value === null || typeof value !== 'object') return value;
  if (depth >= MAX_DEPTH) return '[Truncated]';
  if (seen.has(value)) return '[Circular]';
  seen.add(value);

  if (Array.isArray(value)) return value.map(item => redact(item, depth + 1, seen));

  return Object.keys(value).reduce((acc, key) => {
    acc[key] = SENSITIVE_KEY.test(key) ? REDACTED : redact(value[key], depth + 1, seen);
    return acc;
  }, {});
};

/**
 * A query value, with personal data removed.
 *
 * Values are decoded first: a postal code arrives as `M5V%202T6`, and the
 * space that makes it recognisable is hidden inside the encoding. A value that
 * needed scrubbing comes back re-encoded; one that did not is returned exactly
 * as it was, so ordinary URLs are not rewritten.
 */
const scrubQueryValue = raw => {
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw.replace(/\+/g, ' '));
  } catch (e) {
    return scrubText(raw);
  }
  const scrubbed = scrubText(decoded);
  return scrubbed === decoded ? raw : encodeURIComponent(scrubbed);
};

export const scrubUrl = url => {
  if (typeof url !== 'string' || !url) return url;
  const [withoutFragment] = url.split('#');
  const [base, query] = withoutFragment.split('?');
  const cleanBase = base.replace(/\/\/[^/@]*@/, '//');
  if (!query) return scrubText(cleanBase);

  const cleanQuery = query
    .split('&')
    .map(pair => {
      const [key, ...rest] = pair.split('=');
      let decoded = key;
      try {
        decoded = decodeURIComponent(key);
      } catch (e) {
        // A malformed key is kept as it is and tested as it is.
      }
      return SENSITIVE_KEY.test(decoded)
        ? `${key}=${REDACTED}`
        : `${key}=${scrubQueryValue(rest.join('='))}`;
    })
    .join('&');

  return `${scrubText(cleanBase)}?${cleanQuery}`;
};

const filterHeaders = headers =>
  Object.keys(headers || {}).reduce((acc, name) => {
    if (!SENSITIVE_HEADERS.includes(name.toLowerCase())) acc[name] = headers[name];
    return acc;
  }, {});

export const scrubBreadcrumb = crumb => {
  if (!crumb) return crumb;
  const next = { ...crumb };
  if (typeof next.message === 'string') next.message = scrubText(next.message);
  if (next.data) {
    const data = redact(next.data);
    if (typeof next.data.url === 'string') data.url = scrubUrl(next.data.url);
    if (typeof next.data.to === 'string') data.to = scrubUrl(next.data.to);
    if (typeof next.data.from === 'string') data.from = scrubUrl(next.data.from);
    next.data = data;
  }
  return next;
};

export const scrubEvent = event => {
  if (!event) return event;

  if (event.request) {
    const request = event.request;
    delete request.cookies;
    delete request.data;
    if (request.headers) request.headers = filterHeaders(request.headers);
    if (request.url) request.url = scrubUrl(request.url);
    if (typeof request.query_string === 'string') {
      request.query_string = scrubUrl(`?${request.query_string}`).slice(1);
    } else if (request.query_string) {
      request.query_string = redact(request.query_string);
    }
  }

  if (event.user) {
    event.user = event.user.id ? { id: event.user.id } : undefined;
  }

  if (typeof event.message === 'string') event.message = scrubText(event.message);
  if (event.exception && Array.isArray(event.exception.values)) {
    event.exception.values.forEach(entry => {
      if (typeof entry.value === 'string') entry.value = scrubText(entry.value);
    });
  }

  if (event.extra) event.extra = redact(event.extra);
  if (event.contexts) event.contexts = redact(event.contexts);
  if (event.tags) {
    event.tags = Object.keys(event.tags).reduce((acc, key) => {
      acc[key] = typeof event.tags[key] === 'string' ? scrubText(event.tags[key]) : event.tags[key];
      return acc;
    }, {});
  }
  if (Array.isArray(event.breadcrumbs)) event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);

  return event;
};

export const scrubEventOrDrop = event => {
  try {
    return scrubEvent(event);
  } catch (e) {
    return null;
  }
};

export const scrubBreadcrumbOrDrop = crumb => {
  try {
    return scrubBreadcrumb(crumb);
  } catch (e) {
    return null;
  }
};
